import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  Renderer2,
  inject,
} from '@angular/core';
import { documentOf, windowOf } from '../anchor-document.util';

type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end';
type TXPosition = 'start' | 'center' | 'end';

/**
 * Directive that displays any HTMLElement as a static floating tooltip.
 *
 * Attach it to the host element and pass the tooltip element reference via the input.
 * The tooltip element will be positioned using `position: fixed` near the host element.
 * The tooltip remains visible while hovering over the host, tooltip, or the area between them.
 */
@Directive({
  selector: '[libTooltipStatic]',
  standalone: true,
  exportAs: 'tooltipStaticDirective',
})
export class TooltipStaticDirective implements OnDestroy {
  /**
   * Tracks which directive instance currently "owns" each shared tooltip element.
   * When multiple directive instances reference the same tooltip DOM node (e.g. grid rows),
   * only the active owner is allowed to hide it, preventing race conditions.
   */
  private static activeOwnerByElement = new WeakMap<HTMLElement, TooltipStaticDirective>();

  /**
   * Tooltip element that will be shown while the user hovers the host element.
   */
  @Input('libTooltipStatic')
  set tooltip(value: ElementRef<HTMLElement> | HTMLElement | null | undefined) {
    this.tooltipElement = this.resolveElement(value);
    if (this.tooltipElement) {
      this.prepareTooltipElement();
    }
  }

  /** Allows disabling the tooltip behaviour without removing the directive. */
  @Input() tooltipStaticDisabled = false;
  /** Custom z-index applied to the floating tooltip. */
  @Input() tooltipStaticZIndex = 1000;
  /** Horizontal position of the tooltip relative to the host element. */
  @Input() xAxis: TXPosition = 'start';
  /** Vertical position of the tooltip relative to the host element. */
  @Input() yAxis: TYPosition = 'end';
  /** Whether to add distance between tooltip and host element. */
  @Input() hasDistance = true;
  /** Distance in pixels between tooltip and host element. */
  @Input() distance = 6;

  /** Emits true when tooltip is active/visible, false when hidden. */
  @Output() tooltipActiveChange = new EventEmitter<boolean>();

  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly hostElement = inject(ElementRef);

  private tooltipElement: HTMLElement | null = null;
  private tooltipInitialDisplay: string | null = null;
  private readonly windowRef = this.document?.defaultView ?? null;
  private isHoveringTooltip = false;
  private isHoveringHost = false;
  /** `body` onde o balão está montado — o do documento da âncora na altura. */
  private appendedTo: HTMLElement | null = null;

  /**
   * Documento da âncora. Não é sempre o global: no mini-monitor destacado
   * (Document Picture-in-Picture) a âncora está noutra janela, e é no `body` e
   * na viewport dela que o balão tem de aparecer. Lido a cada uso porque a
   * âncora pode mudar de documento enquanto a diretiva vive.
   */
  private get anchorDocument(): Document {
    return documentOf(this.hostElement.nativeElement) ?? this.document;
  }

  private get anchorWindow(): Window | null {
    return this.anchorDocument.defaultView ?? this.windowRef;
  }
  private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  /** Frame loop that keeps the balloon next to the host while it is open. */
  private trackingFrameId: number | null = null;
  private trackingView: Window | null = null;
  /** Host position on the last frame, to reposition only when it actually moved. */
  private lastHostRect: { top: number; left: number; width: number; height: number } | null = null;

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    if (this.tooltipStaticDisabled || !this.tooltipElement || !this.anchorWindow) {
      return;
    }

    this.isHoveringHost = true;
    this.showTooltip(event);
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(_event: MouseEvent): void {
    this.isHoveringHost = false;

    this.clearHideTimeout();
    this.hideTimeoutId = setTimeout(() => {
      if (!this.isHoveringTooltip && !this.isHoveringHost) {
        this.hideTooltip();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.stopTracking();
    this.hideTooltip(true);
    
    if (this.appendedTo && this.tooltipElement?.parentNode === this.appendedTo) {
      this.renderer.removeChild(this.appendedTo, this.tooltipElement);
    }
    this.appendedTo = null;
  }

  /** Ensure tooltip has the required base styles and cached display value. */
  private prepareTooltipElement(): void {
    const view = windowOf(this.tooltipElement) ?? this.anchorWindow;
    if (!this.tooltipElement || !view) {
      return;
    }

    const computedStyles = view.getComputedStyle(this.tooltipElement);
    if (!this.tooltipInitialDisplay) {
      this.tooltipInitialDisplay =
        computedStyles.display !== 'none' ? computedStyles.display : 'block';
    }

    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(this.tooltipElement, 'top', '0');
    this.renderer.setStyle(this.tooltipElement, 'left', '0');
    this.renderer.setStyle(this.tooltipElement, 'pointer-events', 'auto');
    this.renderer.setStyle(
      this.tooltipElement,
      'z-index',
      String(this.tooltipStaticZIndex)
    );
    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');
    this.renderer.setStyle(this.tooltipElement, 'top', '-9999px');
    this.renderer.setStyle(this.tooltipElement, 'left', '-9999px');
  }

  /** Display tooltip and align it with the host element. */
  public showTooltip(_event?: MouseEvent): void {
    if (!this.tooltipElement) {
      return;
    }

    TooltipStaticDirective.activeOwnerByElement.set(this.tooltipElement, this);
    this.clearHideTimeout();

    this.prepareTooltipElement();
    
    this.attachTooltipListeners();

    const body = this.anchorDocument.body;
    if (body && this.tooltipElement.parentNode !== body) {
      this.renderer.appendChild(body, this.tooltipElement);
      this.appendedTo = body;
    }
    
    this.renderer.setStyle(
      this.tooltipElement,
      'display',
      this.tooltipInitialDisplay ?? 'block'
    );
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'visible');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '1');

    this.tooltipActiveChange.emit(true);

    (this.anchorWindow ?? window).requestAnimationFrame(() => {
      this.updateTooltipPosition();
    });

    this.startTracking();
  }

  /**
   * Acompanha a âncora frame a frame enquanto o balão está aberto. A posição só
   * era calculada ao abrir: com scroll (ou qualquer reflow) a âncora andava e o
   * balão ficava parado onde ela estava. Não há evento que cubra todos os casos —
   * scroll de qualquer antepassado, inércia do trackpad, mudança de layout —, por
   * isso compara o rect da âncora a cada frame e só reposiciona quando mudou.
   */
  private startTracking(): void {
    if (this.trackingFrameId !== null) {
      return;
    }

    this.trackingView = this.anchorWindow ?? window;
    this.lastHostRect = null;
    this.trackingFrameId = this.trackingView.requestAnimationFrame(this.trackHost);
  }

  private stopTracking(): void {
    if (this.trackingFrameId !== null) {
      this.trackingView?.cancelAnimationFrame(this.trackingFrameId);
    }
    this.trackingFrameId = null;
    this.trackingView = null;
    this.lastHostRect = null;
  }

  private readonly trackHost = (): void => {
    // Outra instância passou a ser dona do mesmo balão (ex.: linhas de grid).
    if (
      !this.trackingView ||
      !this.tooltipElement ||
      TooltipStaticDirective.activeOwnerByElement.get(this.tooltipElement) !== this
    ) {
      this.stopTracking();
      return;
    }

    const { top, left, width, height } = this.hostElement.nativeElement.getBoundingClientRect();
    const last = this.lastHostRect;
    if (last && (last.top !== top || last.left !== left || last.width !== width || last.height !== height)) {
      this.updateTooltipPosition();
    }
    this.lastHostRect = { top, left, width, height };

    this.trackingFrameId = this.trackingView.requestAnimationFrame(this.trackHost);
  };

  /** Hide tooltip and cleanup listeners. */
  public hideTooltip(forceCleanup: boolean = false): void {
    this.clearHideTimeout();
    this.detachTooltipListeners();
    this.stopTracking();

    if (!this.tooltipElement) {
      return;
    }

    if (
      !forceCleanup &&
      TooltipStaticDirective.activeOwnerByElement.get(this.tooltipElement) !== this
    ) {
      return;
    }

    this.renderer.setStyle(this.tooltipElement, 'display', 'none');
    this.renderer.setStyle(this.tooltipElement, 'visibility', 'hidden');
    this.renderer.setStyle(this.tooltipElement, 'opacity', '0');

    this.tooltipActiveChange.emit(false);

    if (forceCleanup) {
      if (this.appendedTo && this.tooltipElement.parentNode === this.appendedTo) {
        this.renderer.removeChild(this.appendedTo, this.tooltipElement);
        this.appendedTo = null;
      }

      this.renderer.removeStyle(this.tooltipElement, 'pointer-events');
      this.renderer.removeStyle(this.tooltipElement, 'top');
      this.renderer.removeStyle(this.tooltipElement, 'left');
      this.renderer.removeStyle(this.tooltipElement, 'position');
      this.renderer.removeStyle(this.tooltipElement, 'z-index');
      this.renderer.removeStyle(this.tooltipElement, 'visibility');
      this.renderer.removeStyle(this.tooltipElement, 'opacity');
      if (this.tooltipInitialDisplay) {
        this.renderer.setStyle(
          this.tooltipElement,
          'display',
          this.tooltipInitialDisplay
        );
      } else {
        this.renderer.removeStyle(this.tooltipElement, 'display');
      }
    }
  }

  /** Update tooltip coordinates based on host element position. */
  private updateTooltipPosition(): void {
    const view = this.anchorWindow;
    if (!this.tooltipElement || !this.hostElement || !view) {
      return;
    }

    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const { innerWidth, innerHeight } = view;

    let left = this.calcLeftPosition(
      this.xAxis,
      this.yAxis,
      this.tooltipElement
    );
    let top = this.calcTopPosition(this.yAxis, this.tooltipElement);


    if (top + tooltipRect.height > innerHeight) {
      top = this.calcTopPosition('start', this.tooltipElement);
    }
    if (top < 0) {
      top = this.calcTopPosition('end', this.tooltipElement);
    }

    if (left + tooltipRect.width > innerWidth && this.xAxis === 'end') {
      left = this.calcLeftPosition('start', this.yAxis, this.tooltipElement);
    }
    if (
      left < 0 ||
      (left + tooltipRect.width > innerWidth && this.xAxis === 'start')
    ) {
      left = this.calcLeftPosition('end', this.yAxis, this.tooltipElement);
    }



       
    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    this.renderer.removeStyle(this.tooltipElement, 'transform');
  }

  private calcTopPosition(
    position: TYPosition,
    tooltip: HTMLElement
  ): number {
    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const distance = this.hasDistance ? this.distance : 0;

    let result: number;
    switch (position) {
      case 'start':
        result = hostRect.top - tooltipRect.height - distance;
        break;

      case 'center':
        result = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2;
        break;

      case 'end':
        result = hostRect.top + hostRect.height + distance;
        break;

      default:
        result = hostRect.top - tooltipRect.height - distance;
    }

    return result;
  }

  private calcLeftPosition(
    positionX: TXPosition,
    positionY: TYPosition,
    tooltip: HTMLElement
  ): number {
    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const distance = this.hasDistance ? this.distance : 0;

    let result: number;
    switch (positionX) {
      case 'start':
        result = positionY === 'center'
          ? hostRect.left - tooltipRect.width - distance
          : hostRect.left;
        break;

      case 'center':
        result = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2;
        break;

      case 'end':
        result = positionY === 'center'
          ? hostRect.left + hostRect.width + distance
          : hostRect.left + hostRect.width - tooltipRect.width;
        break;

      default:
        result = positionY === 'center'
          ? hostRect.left - tooltipRect.width - distance
          : hostRect.left;
    }


    return result;
  }

  /** Attach mouse event listeners to the tooltip element. */
  private attachTooltipListeners(): void {
    if (!this.tooltipElement) {
      return;
    }

    this.tooltipElement.addEventListener(
      'mouseenter',
      this.handleTooltipMouseEnter
    );
    this.tooltipElement.addEventListener(
      'mouseleave',
      this.handleTooltipMouseLeave
    );
  }

  /** Detach mouse event listeners from the tooltip element. */
  private detachTooltipListeners(): void {
    if (!this.tooltipElement) {
      return;
    }

    this.tooltipElement.removeEventListener(
      'mouseenter',
      this.handleTooltipMouseEnter
    );
    this.tooltipElement.removeEventListener(
      'mouseleave',
      this.handleTooltipMouseLeave
    );
  }

  private readonly handleTooltipMouseEnter = (): void => {
    this.isHoveringTooltip = true;
  };

  private readonly handleTooltipMouseLeave = (): void => {
    this.isHoveringTooltip = false;

    this.clearHideTimeout();
    this.hideTimeoutId = setTimeout(() => {
      if (!this.isHoveringHost && !this.isHoveringTooltip) {
        this.hideTooltip();
      }
    }, 50);
  };

  /** Clear any pending hide timeout. */
  private clearHideTimeout(): void {
    if (this.hideTimeoutId !== null) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  /** Check if mouse is in the area between host element and tooltip. */
  private isMouseInTooltipArea(event: MouseEvent): boolean {
    if (!this.tooltipElement || !this.hostElement) {
      return false;
    }

    const hostRect = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    const mouseX = event.clientX;
    const mouseY = event.clientY;

    const isInHorizontalRange =
      mouseX >= Math.min(hostRect.left, tooltipRect.left) &&
      mouseX <= Math.max(hostRect.right, tooltipRect.right);

    const isInVerticalRange =
      mouseY >= hostRect.bottom && mouseY <= tooltipRect.top;

    return isInHorizontalRange && isInVerticalRange;
  }

  private resolveElement(
    value?: ElementRef<HTMLElement> | HTMLElement | null
  ): HTMLElement | null {
    if (!value) {
      return null;
    }

    if (value instanceof ElementRef) {
      return value.nativeElement;
    }

    return value;
  }
}
